/**
 * useCurrencyHelpers Hook
 * 
 * Provides helper functions for currency operations.
 */

import { Currency, Account } from "../../../types";
import { Timestamp, FieldValue, QuerySnapshot } from "firebase/firestore";
import { COMMON_CURRENCIES } from "./useDefaultCurrency";

export interface UseCurrencyHelpersParams {
  currencies: Currency[];
  accounts: Account[];
  defaultCurrency?: string;
}

export interface UseCurrencyHelpersReturn {
  getAvailableCurrencies: () => typeof COMMON_CURRENCIES;
  isCurrencyInUse: (currencyCode: string) => boolean;
  getAccountsUsingCurrency: (currencyCode: string) => Account[];
  formatRate: (rate: number | string) => string;
  formatDate: (date: Timestamp | FieldValue | undefined) => string;
  fetchExchangeRatesFromAPI: (baseCurrency: string, targetCurrencies: string[]) => Promise<Record<string, number | null>>;
  hasManualEdits: (currenciesSnapshot: QuerySnapshot) => boolean;
}

/**
 * Hook for currency helper functions
 */
export const useCurrencyHelpers = ({ currencies, accounts, defaultCurrency }: UseCurrencyHelpersParams): UseCurrencyHelpersReturn => {
  // Get available currencies (not yet added, and not the default currency)
  const getAvailableCurrencies = (): typeof COMMON_CURRENCIES => {
    const addedCodes = new Set(currencies.map((c) => c.code));
    return COMMON_CURRENCIES.filter((c) => !addedCodes.has(c.code) && c.code !== defaultCurrency);
  };

  // Check if currency is used in accounts
  const isCurrencyInUse = (currencyCode: string): boolean => {
    return accounts.some((acc) => acc.currency === currencyCode);
  };

  // Get accounts using a currency
  const getAccountsUsingCurrency = (currencyCode: string): Account[] => {
    return accounts.filter((acc) => acc.currency === currencyCode);
  };

  // Format rate without trailing zeros, max 5 decimal places
  const formatRate = (rate: number | string): string => {
    const numRate = typeof rate === "string" ? parseFloat(rate) : rate;
    if (isNaN(numRate)) return "0";
    // Round to 5 decimal places and remove trailing zeros
    const rounded = Math.round(numRate * 100000) / 100000;
    // If it's a whole number, return without decimals
    if (rounded % 1 === 0) {
      return rounded.toString();
    }
    // Format with up to 5 decimal places and remove trailing zeros
    return rounded.toFixed(5).replace(/\.?0+$/, "");
  };

  // Format date
  const formatDate = (date: Timestamp | FieldValue | undefined): string => {
    if (!date) return "Never";
    if (typeof (date as Timestamp).toDate === "function") {
      return (date as Timestamp).toDate().toLocaleDateString();
    }
    return "Never";
  };

  // Fetch exchange rates from API
  const fetchExchangeRatesFromAPI = async (baseCurrency: string, targetCurrencies: string[]): Promise<Record<string, number | null>> => {
    try {
      // Use exchangerate-api.com free endpoint (no API key required)
      const response = await fetch(`https://api.exchangerate-api.com/v4/latest/${baseCurrency}`);
      
      if (!response.ok) {
        console.error("Failed to fetch exchange rates from API");
        return {};
      }

      const data = await response.json();
      const rates: Record<string, number | null> = {};

      // Extract rates for target currencies
      targetCurrencies.forEach((currencyCode) => {
        if (currencyCode === baseCurrency) {
          rates[currencyCode] = 1.0;
        } else if (data.rates && data.rates[currencyCode]) {
          rates[currencyCode] = data.rates[currencyCode] as number;
        } else {
          rates[currencyCode] = null; // Rate not available
        }
      });

      return rates;
    } catch (error) {
      console.error("Error fetching exchange rates from API:", error);
      return {};
    }
  };

  // Check if any currency has been manually edited
  const hasManualEdits = (currenciesSnapshot: QuerySnapshot): boolean => {
    return currenciesSnapshot.docs.some((docSnap) => {
      const data = docSnap.data();
      return data.manuallyEdited === true;
    });
  };

  return {
    getAvailableCurrencies,
    isCurrencyInUse,
    getAccountsUsingCurrency,
    formatRate,
    formatDate,
    fetchExchangeRatesFromAPI,
    hasManualEdits,
  };
};
